import {
  AlertOctagon,
  AlertTriangle,
  Bug,
  ChevronRight,
  ExternalLink,
  Info,
  Sparkles,
  Wrench,
} from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import type { AnnouncementData } from "@/types";

interface DashboardAnnouncementsProps {
  data: AnnouncementData;
}

// Minimal icon mapping by type (neutral palette)
const typeIcons = {
  feature: Sparkles,
  bugfix: Bug,
  warning: AlertTriangle,
  info: Info,
  maintenance: Wrench,
  critical: AlertOctagon,
} as const;

// Subtle emphasis only for urgency
const priorityAccent: Record<string, string> = {
  low: "",
  normal: "",
  high: "",
  urgent: "before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-red-500",
};

// Style overrides for critical announcements
const typeStyles: Record<string, { container: string; icon: string }> = {
  critical: {
    container: "bg-red-50 border-red-200",
    icon: "border-red-200 bg-red-100 text-red-600",
  },
};

export default function DashboardAnnouncements({ data }: DashboardAnnouncementsProps) {
  // Filtrar anuncios para dashboard
  const dashboardAnnouncements = data.announcements.filter(
    (announcement) => 
      announcement.position === "dashboard" || announcement.position === "both"
  );

  // Marcar como vista la versión cuando el componente se monta
  useEffect(() => {
    if (data.version) {
      localStorage.setItem("announcements_last_seen_version", data.version);
      // Disparar evento personalizado para notificar el cambio
      document.dispatchEvent(new CustomEvent("announcementsSeen"));
    }
  }, [data.version]);

  if (dashboardAnnouncements.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <div className="mx-auto flex size-9 items-center justify-center rounded-full bg-slate-100">
            <Info className="h-4 w-4 text-slate-500" />
          </div>
          <p className="text-sm text-slate-600">Todo está actualizado</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("es-ES", {
        month: "short", 
        day: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 sm:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-medium text-slate-900 tracking-tight">Novedades</h2>
            <p className="mt-1 text-sm text-slate-500">Actualizaciones y mejoras recientes</p>
          </div>
          <div className="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600">v{data.version}</div>
        </div>

        {/* Announcements list */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <ul className="divide-y divide-slate-200">
            {dashboardAnnouncements.map((announcement) => {
              const IconComponent = typeIcons[announcement.type] ?? Info;
              const accent = priorityAccent[announcement.priority] ?? "";
              const customStyles = typeStyles[announcement.type];

              return (
                <li
                  key={announcement.id}
                  className={`relative ${accent} ${customStyles?.container ?? ""}`}
                >
                  <div className="flex items-start gap-4 px-4 py-4 sm:px-5">
                    {/* Icon */}
                    <div className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border ${customStyles?.icon ?? "border-slate-200 bg-slate-50 text-slate-600"}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-medium text-slate-900">
                            {announcement.title}
                          </h3>
                          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                            {announcement.content}
                          </p>
                        </div>

                        {/* Date */}
                        <div className="shrink-0 pt-0.5 text-right text-xs text-slate-500">
                          {formatDate(announcement.datePublished)}
                        </div>
                      </div>

                      {/* Action */}
                      {announcement.action && (
                        <div className="mt-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            asChild
                          >
                            <a
                              href={announcement.action.url}
                              target={announcement.action.target ?? "_self"}
                              rel={announcement.action.target === "_blank" ? "noopener noreferrer" : undefined}
                              className="inline-flex items-center gap-1.5"
                            >
                              <span>{announcement.action.label}</span>
                              {announcement.action.target === "_blank" ? (
                                <ExternalLink className="h-3.5 w-3.5 text-slate-500" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
                              )}
                            </a>
                          </Button>
                        </div>
                      )}

                      {/* Tags */}
                      {announcement.tags && announcement.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {announcement.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Urgent subtle dot */}
                  {announcement.priority === "urgent" && (
                    <span className="absolute right-4 top-4 inline-flex size-1.5 rounded-full bg-red-500" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
